import { Box } from '@mui/material';
import styled from 'styled-components';
import {
  CustomBarChart,
  Typography,
  colors,
  IconGraphy,
  Avatar,
  styles,
  Dropdown,
} from '@leapeasy/ui-kit';
import { useEffect, useMemo, useState } from 'react';
import { ReactComponent as DashboardEmptyStateIcon } from 'assets/dashboard-empty-state.svg';
import { chartSeriesHasValues } from 'utils/chartSeriesHasValues';

export const GrossRentChart = ({ data, onNavigateApplications, onNavigatePolicies }) => {
  const toYear = (value) => {
    const rawValue = value && typeof value === 'object' && 'value' in value ? value.value : value;
    const parsed = Number(rawValue);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const monthlyRentByYear = useMemo(() => {
    const source = data?.monthRentData;
    if (!source || typeof source !== 'object') return {};

    const mapped = {};
    Object.entries(source).forEach(([yearKey, monthValues]) => {
      const year = toYear(yearKey);
      if (!year || !Array.isArray(monthValues)) return;
      mapped[year] = monthValues;
    });

    return mapped;
  }, [data?.monthRentData]);

  const yearList = useMemo(
    () =>
      Object.keys(monthlyRentByYear)
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value))
        .sort((a, b) => a - b),
    [monthlyRentByYear],
  );

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  useEffect(() => {
    if (!yearList.length) return;
    if (!yearList.includes(Number(year))) {
      setYear(yearList[yearList.length - 1]);
    }
  }, [year, yearList]);

  const reportData = Array.isArray(monthlyRentByYear[Number(year)])
    ? monthlyRentByYear[Number(year)]
    : [];
  const dropdownYears = yearList.length ? yearList : [currentYear];
  const monthlyHasValues = chartSeriesHasValues(reportData, 'Rent');
  const yearlyHasValues = chartSeriesHasValues(data?.yearRentData, 'Rent');

  return (
    <Box
      sx={{
        boxShadow: styles.boxShadow.sm,
        borderRadius: `${styles.borderRadius.large}px`,
        border: `1px solid ${colors.black[300]}`,
        width: '100%',
        padding: '16px',
        boxSizing: 'border-box',
      }}
      display="flex"
      flexDirection="column"
      gap="12px"
    >
      <Box display="flex" gap="16px">
        <Avatar size="large" iconImage={<IconGraphy icon="FileFolder.Contract" />} />
        <Box>
          <Typography variant="h4" style={{ color: colors.purple[900], fontWeight: '400' }}>
            Gross Rent Supported by Leap
          </Typography>

          <Typography variant="body3" style={{ color: colors.neutral[900], fontWeight: '400' }}>
            Monthly and yearly trends of gross rent supported by Leap.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          background: '#f9f8f9',
          border: `1px solid ${colors.purple[100]}`,
          borderRadius: '8px',
          padding: '16px',
          width: '100%',
          boxSizing: 'border-box',
        }}
        display="flex"
        flexWrap="wrap"
        gap="20px"
      >
        <Box
          sx={{
            flex: '1.8 1 0%',
            background: 'white',
            border: `1px solid ${colors.black[200]}`,
            borderRadius: `${styles.borderRadius.large}px`,
            padding: '20px',
            boxSizing: 'border-box',
          }}
          display="flex"
          flexDirection="column"
          gap="24px"
        >
          <Box display="flex" justifyContent="space-between" width="100%">
            <Box display="flex" gap="4px" flexDirection="column">
              <Typography variant="body1" style={{ fontWeight: '600', color: colors.purple[900] }}>
                Monthly
              </Typography>
              <Typography variant="body3" style={{ color: colors.neutral[900] }}>
                Monitor annual Gross Rent trends.
              </Typography>
            </Box>

            <Dropdown
              value={year}
              size="medium"
              onChange={(e) => {
                const nextYear = toYear(e?.target?.value);
                if (nextYear) {
                  setYear(nextYear);
                }
              }}
              options={dropdownYears.map((yearOption) => ({
                text: yearOption.toString(),
                value: yearOption,
              }))}
              multiselect={false}
              placeholder="Select item"
              showSearch={false}
              icon={<IconGraphy icon="Finance.Payments" />}
              iconVisible={true}
              label="Year"
            />
          </Box>

          {monthlyHasValues ? (
            <CustomBarChart
              chartWidth="100%"
              chartHeight={200}
              data={reportData}
              colors={{
                Rent: colors.purpleIrish[100],
              }}
            />
          ) : (
            <ChartEmptyBody>
              <DashboardEmptyStateIcon />
              <Typography
                variant="body2"
                style={{
                  textAlign: 'center',
                  color: colors.neutral[900],
                  maxWidth: '360px',
                  fontWeight: '500',
                }}
              >
                No data for this period
              </Typography>
            </ChartEmptyBody>
          )}
        </Box>

        <Box
          sx={{
            flex: '1 1 0%',
            background: 'white',
            border: `1px solid ${colors.black[200]}`,
            borderRadius: `${styles.borderRadius.large}px`,
            padding: '20px',
            boxSizing: 'border-box',
          }}
          display="flex"
          justifyContent="center"
          flexDirection="column"
          gap="20px"
        >
          <Typography variant="body1" style={{ fontWeight: '600' }}>
            Yearly
          </Typography>

          {yearlyHasValues ? (
            <CustomBarChart
              chartWidth="100%"
              chartHeight={220}
              data={data.yearRentData}
              colors={{
                Rent: colors.purpleIrish[100],
              }}
              barChartProps={{
                margin: { top: 4, right: 8, left: 0, bottom: 28 },
              }}
              xAxisProps={{
                interval: 0,
                angle: -35,
                textAnchor: 'end',
                height: 48,
                tick: { fill: '#A39AA9', fontSize: 10, fontWeight: 500 },
              }}
            />
          ) : (
            <ChartEmptyBody>
              <DashboardEmptyStateIcon />
              <Typography
                variant="body2"
                style={{
                  textAlign: 'center',
                  color: colors.neutral[900],
                  maxWidth: '360px',
                  fontWeight: '500',
                }}
              >
                No data for this period
              </Typography>
            </ChartEmptyBody>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ChartEmptyBody = styled(Box)`
  width: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-sizing: border-box;
`;
